import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite') || searchParams.get('token');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!inviteToken) {
      toast.error('Kein Einladungslink vorhanden');
      navigate('/');
    }
  }, [inviteToken, navigate]);

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.name) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/auth/register', {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        invite_token: inviteToken
      });

      if (response.data.success) {
        toast.success('Konto erfolgreich erstellt!');
        navigate('/dashboard', { state: { user: response.data.user } });
      }
    } catch (error) {
      console.error('Registration error:', error);
      const errorMsg = error.response?.data?.detail || 'Registrierung fehlgeschlagen';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1662567430100-d173bdaad866?crop=entropy&cs=srgb&fm=jpg&q=85)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>
      
      <Card className="relative z-10 w-full max-w-md bg-zinc-900/90 border-zinc-800 backdrop-blur-xl" data-testid="register-card">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto">
            <img 
              src="https://customer-assets.emergentagent.com/job_70aa5bcf-098c-4523-b0d6-5bbb87ecc869/artifacts/ysjl1nto_IMG_8154.jpeg"
              alt="ZOZO Logo"
              className="h-24 mx-auto"
            />
          </div>
          <CardTitle className="text-3xl font-black uppercase tracking-tight text-white">
            Konto erstellen
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Willkommen bei ZOZO Fleet Command
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-zinc-300">Name</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Ihr vollständiger Name"
                className="bg-zinc-950 border-zinc-800 text-white"
                data-testid="name-input"
                disabled={loading}
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-zinc-300">E-Mail</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="ihre.email@zozo-burger.de"
                className="bg-zinc-950 border-zinc-800 text-white"
                data-testid="email-input"
                disabled={loading}
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-zinc-300">Passwort</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder="Mindestens 6 Zeichen"
                className="bg-zinc-950 border-zinc-800 text-white"
                data-testid="password-input"
                disabled={loading}
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword" className="text-zinc-300">Passwort bestätigen</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                placeholder="Passwort wiederholen"
                className="bg-zinc-950 border-zinc-800 text-white"
                data-testid="confirm-password-input"
                disabled={loading}
              />
            </div>
            <Button 
              type="submit"
              className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wide"
              data-testid="register-btn"
              disabled={loading}
            >
              <UserPlus className="mr-2 h-5 w-5" />
              {loading ? 'Konto wird erstellt...' : 'Konto erstellen'}
            </Button>
          </form>
          
          <div className="text-xs text-center text-zinc-500 mt-4">
            Mit der Registrierung akzeptieren Sie die Nutzungsbedingungen
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
